// CAMIS booking strategy (ParksCanada, BCParks, OntarioParks, etc.).
//
// This module is the source of truth for the CAMIS reserve URL shape.
// `utils/availabilityRequest.js` re-exports `buildCamisReserveUrl` from
// here for legacy callers (unit tests) that imported it by name.

import { add, format } from "date-fns";
import { resolveMinStayLength, resolveMinCapacity } from "./_context.js";

// TODO: Move this to the scraper during next round of rescraping.
// Found in the API response at /api/capacitycategory/capacitycategories
// Or in the URL bar after searching for availability.
// -32768 is the "Total Party Size" one that works for all CAMIS agencies.
const CAMIS_PARTY_SIZE_CATEGORY_ID = -32768;

// A tent-pad request stores a pad count, not a headcount, so the link can
// only send the site's own party floor. CAMIS asks for the real party at
// checkout; changing it on the results page forces a new search instead.
function tentPadPartyCount(site) {
  return site.extraAttributes?.minPartySize || 1;
}

/**
 * Build a CAMIS reserve URL for a site on a specific stay.
 * Scrubs pre-existing partySize/nights params from the stored reserveUrl
 * and encodes peopleCapacityCategoryCounts + startDate/endDate instead.
 */
export function buildCamisReserveUrl(
  site,
  { startDate, minStayLength, minCapacity = 1 },
) {
  const url = new URL(site.reserveUrl);
  url.searchParams.delete("partySize");
  url.searchParams.delete("nights");

  if (site.capacityUnit === "tent_pads") {
    // Tent pad sites (e.g. Kootenay/Yoho backcountry zones) have
    // equipmentCapacity separate from party size.
    url.searchParams.set("equipmentCapacity", String(minCapacity));
    url.searchParams.set(
      "peopleCapacityCategoryCounts",
      `[[${CAMIS_PARTY_SIZE_CATEGORY_ID},null,${tentPadPartyCount(site)},null]]`,
    );
  } else {
    // People/seats/default — capacity maps to party size count. Floor it at
    // the site's own minimum: group campsites reject a party below their
    // minCapacity (9-25) and return an empty results page, so a request's
    // small party size (often 1) would produce a dead reserve link.
    const partyCount = Math.max(
      minCapacity,
      site.extraAttributes?.minPartySize || 1,
    );
    url.searchParams.set(
      "peopleCapacityCategoryCounts",
      `[[${CAMIS_PARTY_SIZE_CATEGORY_ID},null,${partyCount},null]]`,
    );
  }

  url.searchParams.set("startDate", format(startDate, "yyyy-MM-dd"));
  url.searchParams.set(
    "endDate",
    format(add(startDate, { days: minStayLength }), "yyyy-MM-dd"),
  );

  return url.toString();
}

export default {
  // Campers with a bigger group think they must raise party size before
  // reserving, and the new search that takes can cost them the opening.
  // Parks Canada and BC Parks, the CAMIS portals with tent pads, both take
  // the real headcount on a "Your Party" step at checkout.
  bookingInstructions({ site }) {
    if (site.capacityUnit !== "tent_pads") return null;
    return `Your tent pads are already filled in. Leave party size at ${tentPadPartyCount(site)} and reserve the site. You'll enter your full party at checkout, so there's no need to search again.`;
  },

  buildBookingUrl(ctx) {
    const { site, arrivalDate } = ctx;
    return buildCamisReserveUrl(site, {
      startDate: arrivalDate,
      minStayLength: resolveMinStayLength(ctx),
      minCapacity: resolveMinCapacity(ctx),
    });
  },

  // CAMIS site.reserveUrl is the booking results page with map info —
  // it works as a preview link as-is.
  buildPreviewUrl(site) {
    return site.reserveUrl;
  },

  describeBookingInputs() {
    return {
      inputs: {
        arrivalDate: { status: "required" },
        departureDate: { status: "required" },
        ref: {
          status: "ignored",
          notes:
            "CAMIS uses site.reserveUrl (which already carries resourceLocationId/mapId); ref is not encoded.",
        },
        partySize: {
          status: "optional",
          defaultValue: 1,
          notes:
            "Encoded into peopleCapacityCategoryCounts; defaults to 1 when omitted.",
        },
      },
    };
  },
};
